import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, X } from 'lucide-react';
import './PageLayout.css';
import Navbar from './UI/Sections/Navbar/navbar.section';
import smallLogo from './DEEPECHO_logo_small.png';
import largeLogo from './DEEPECHO logo.png';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './layout/Layout';
import { HelpProvider } from './help/HelpProvider';
import { usePermissions } from './helpers/acl/permissions';

export const deepechologo = {
  small: smallLogo,
  large: largeLogo,
};

const API =
  (window as any).RUNTIME_ENV?.BACKEND_URL ??
  process.env.BACKEND_URL ??
  'http://localhost:8000/api/v1';

let billingNudgeCheckedThisLoad = false;

type BillingStatus = {
  subscription_status: string;
  trial_days_remaining: number | null;
  billing_type?: string;
};

async function fetchBillingStatus(): Promise<BillingStatus> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  const response = await fetch(`${API}/billing/status`, {
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

const AppLayout = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('Organization');
  const { can, isLoading: permissionsLoading } = usePermissions();
  const [billingNudge, setBillingNudge] = React.useState<BillingStatus | null>(null);

  useEffect(() => {
    // Returning from a successful Stripe Checkout: don't nag the user who just paid. The
    // webhook lands moments later, so suppress the nudge for this load and clean the URL.
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      billingNudgeCheckedThisLoad = true;
      params.delete('billing');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : '') + window.location.hash);
      return;
    }
    if (billingNudgeCheckedThisLoad || permissionsLoading || !can('org_unit.manage')) return;
    billingNudgeCheckedThisLoad = true;

    fetchBillingStatus()
      .then((status) => {
        // A grant tenant is DeepEcho-sponsored: its licence is not driven by billing, so it has
        // no subscription by design and must never be nudged to subscribe. The backoffice sends
        // billing_type; it defaults to "paid" there, so an unreachable backoffice keeps the nudge
        // for real paying customers rather than silently suppressing it.
        if (
          !['grant', 'training'].includes(status.billing_type ?? '') &&
          status.subscription_status !== 'active' &&
          status.subscription_status !== 'unknown'
        ) {
          setBillingNudge(status);
        }
      })
      .catch(() => {
        // Billing status is non-critical for the shell. Release the once-per-load guard so a
        // transient failure can be retried on a later effect run instead of being stuck this load.
        billingNudgeCheckedThisLoad = false;
      });
  }, [can, permissionsLoading]);

  const trialCopy = (() => {
    if (billingNudge?.subscription_status === 'past_due') {
      return t(
        'billingReminder.pastDue',
        'Your last payment failed. Update your payment method to keep DeepEcho active.'
      );
    }
    const days = billingNudge?.trial_days_remaining;
    if (days == null) {
      return t('billingReminder.subscribe', 'Subscribe to unlock the full DeepEcho experience.');
    }
    if (days <= 0) {
      return t(
        'billingReminder.trialEnded',
        'Your trial has ended. Subscribe to unlock the full DeepEcho experience.'
      );
    }
    // i18next v17 has no _one/_other plurals here, so singular and plural are separate keys.
    if (days === 1) {
      return t(
        'billingReminder.oneDayLeft',
        '1 day left in your free trial. Subscribe to unlock the full DeepEcho experience.'
      );
    }
    return t(
      'billingReminder.daysLeft',
      '{{days}} days left in your free trial. Subscribe to unlock the full DeepEcho experience.',
      { days }
    );
  })();

  return (
    <>
      <HelpProvider>
        <Layout>{props.children}</Layout>
      </HelpProvider>
      {billingNudge ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm dark:bg-black/70">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-amber-200 bg-white shadow-2xl dark:border-amber-900/60 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-6 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                  <CreditCard size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900 dark:text-white">
                    {t('billingReminder.title', 'Subscription setup')}
                  </h2>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    {t('billing.title', 'Organization billing')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBillingNudge(null)}
                className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label={t('billingReminder.dismiss', 'Dismiss billing reminder')}
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-5 p-6">
              <p className="text-sm font-semibold leading-6 text-slate-600 dark:text-slate-300">{trialCopy}</p>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setBillingNudge(null)}
                  className="rounded-xl px-4 py-2 text-sm font-bold text-slate-500 transition hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  {t('billingReminder.notNow', 'Not now')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBillingNudge(null);
                    navigate('/organization?pacsTab=billing');
                  }}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-lg shadow-blue-100 transition hover:bg-blue-700 dark:shadow-none"
                >
                  {t('billingReminder.goToBilling', 'Go to billing')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {/* <Layout
        className="w-full overflow-hidden bg-white"
        style={{ minHeight: '400px', height: '100vh' }}
      >
        <div className="flex h-full">
          <Sidebar />
          <Layout>
            <Content>
              <ToastContainer />
              <div className="h-full w-screen md:w-full">
                {!location.pathname.startsWith('/exams/viewer') && (
                  <div className="bg-[#f7fafe]">
                    <Navbar />
                  </div>
                )}
                <div
                  className={`${location.pathname.startsWith('/exams/viewer') ? 'h-full' : 'h-[calc(100%-49.73px)] py-2'} bg-white`}
                >
                  {props.children}
                </div>
              </div>
            </Content>
          </Layout>
        </div>
      </Layout> */}
    </>
  );
};

export default AppLayout;
